import type { GaiaPayloadConfigs } from "@expert/shared-types";
import { type GuideManagerProps, useGuideManager } from "@expert/solve-tools";
import { useSendExpertAssistMessage } from "@soluto-private/expert-workspace-timeline";
import { useMemo } from "react";
import { env } from "@/config";
import { useVerifiedCustomerStore } from "@/customer-info";
import { useTaskMdn } from "@/customer-panel/components/customer-verification/hooks/useTaskMdn";
import { useGetQueriesBySearchType, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import type { usePartner, VoiceTask } from "@/sdk";
import { userCache } from "@/shared-utils";
import { useAutomaticGuide } from "./useAutomaticGuide";
import { useGuideRecommendation } from "./useGuideRecommendation";

interface PlatformGuideManagerProps
    extends Omit<
        GuideManagerProps,
        "sendMessage" | "requiresTopPadding" | "shouldStartImmediatelyOnSessionChange" | "ssoAccessToken" | "taskMdn"
    > {
    isCustomerSession: boolean;
    partner: ReturnType<typeof usePartner>;
    activeVoiceTask: VoiceTask | null;
    gaiaPayloadProps: GaiaPayloadConfigs;
}

export function usePlatformGuideManager({
    activeVoiceTask,
    callSid,
    isCustomerSession,
    logger: loggerProp,
    partner,
    sessionId,
    gaiaPayloadProps,
    globalConfig,
    ...guideManagerProps
}: Readonly<PlatformGuideManagerProps>) {
    const logger = useMemo(
        () =>
            loggerProp.child({
                globalConfig,
                module: "usePlatformGuideManager",
                partner,
                sessionId,
                supportTeam: "solve",
            }),
        [globalConfig, loggerProp, partner, sessionId],
    );

    const isExternal = env.gaiaExternal;
    const { callControlsEnabled } = usePartnerFeatureConfigured();
    const sendMessage = useSendExpertAssistMessage(logger, isExternal, gaiaPayloadProps);
    const isCustomerVerificationComplete = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete,
    );
    const ssoAccessToken = userCache.ssoAccessToken ?? "";
    const clientChannelId = useGetQueriesBySearchType("QueryFindAgreementsByMDN")?.clientChannelId;

    const taskMdn = useTaskMdn({
        blocklistedNumbers: undefined,
        blocklistedPrefixes: undefined,
    });

    const { openGuide, closeGuide, isGuideOpen, ...guideManagerInfo } = useGuideManager({
        ...guideManagerProps,
        callSid,
        clientChannelId,
        globalConfig,
        logger,
        partner,
        requiresTopPadding: callControlsEnabled,
        sendMessage,
        sessionId,
        ssoAccessToken,
        taskMdn,
    });

    useAutomaticGuide({
        activeVoiceTask,
        isCustomerSession,
        isCustomerVerificationComplete,
        isGuideOpen,
        partner,
        closeGuide,
        openGuide,
        logger,
    });

    useGuideRecommendation({
        callSid,
        isGuideOpen,
        logger,
        openGuide,
        sessionId,
    });

    return { ...guideManagerInfo };
}
